import {
  Modal,
  Box,
  Typography,
  IconButton,
  SxProps,
  Button,
  TextField,
  Collapse,
  Rating,
  CircularProgress,
} from "@mui/material";
import { Dispatch, SetStateAction, useRef, useState } from "react";
import { IAppointment } from "../../../services/api/Appointment/types";
import CloseIcon from "@mui/icons-material/Close";
import { formatDateAndTimeDayDateToString } from "../../../pages/petAndBeauty/Client/ScheduilingPage/timeUtils";
import AppointmentStatusChip from "../AppointmentStatusChip";
import { sharedStyles } from "../../../styles/shared";
import { generalDarkGrey } from "../../../styles/colors";
import { PrimaryButton } from "../buttons/PrimaryButton";
import { useAuth } from "../../../context/AuthProvider/useAuth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { AppointmentStatus, UserRole } from "../../../types/types";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { handleCancelAppointmentClick, handleRateService } from "./functions";
import CheckIcon from "@mui/icons-material/Check";
import useFetchAppointmentRating from "../../../hooks/entities/useFetchAppointmentRating";
import { useTranslation } from "react-i18next";

interface AppointmentDetailsModalProps {
  onClose: VoidFunction;
  onFinish: VoidFunction;
  loading?: boolean;
  appointment: IAppointment;
  onConfirm?: any;
  showClient?: boolean;
  showRating?: boolean;
  setSucceededRatingPost?: Dispatch<SetStateAction<boolean>>;
}

// REFACTOR WITH USE MEMO IN ORDER TO IMPROVE PERFORMACE
export const AppointmentDetailsModal = (
  props: AppointmentDetailsModalProps
) => {
  const { t } = useTranslation();
  const auth = useAuth();

  const { onClose, appointment, onFinish, loading } = props;
  const [wantsToCancel, setWantsToCancel] = useState(false);

  const cancellationReasonRef = useRef<HTMLInputElement>(null);

  const { rating, loadingRating } = useFetchAppointmentRating(appointment?.id);

  const [ratingStars, setRatingStars] = useState(5);

  const ratingCommentRef = useRef<HTMLInputElement>(null);

  const appointmentEmployee = appointment?.serviceEmployee.Employee;

  const isAppointmentFinishable =
    appointment?.status === AppointmentStatus.PENDENT ||
    appointment?.status === AppointmentStatus.CONFIRMED;

  const appointmentItem = (label: string, value: string) => (
    <Typography fontWeight={500}>
     {label}{" "}
     <span style={{ fontWeight: "normal", textAlign: "justify" }}>
        {value}
      </span>
    </Typography>
  );

  const handleOnClose = () => {
    onClose();
    setWantsToCancel(false);
  };

  return (
    <Modal open={appointment !== null} onClose={handleOnClose}>
      <Box sx={styles.container}>
        <IconButton
          onClick={handleOnClose}
          sx={{ margin: "-20px", alignSelf: "flex-end" }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={sharedStyles.verticalContent}>
          <Box
            sx={{
              ...sharedStyles.verticalContent,
              gap: "0px",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">{t("appointmentDetails.title")}</Typography>
            {appointment?.date && (
              <Typography color={generalDarkGrey}>
                    {t("appointmentDetails.scheduledAt", {
                      date: formatDateAndTimeDayDateToString(appointment?.createdAt),
                  })}
              </Typography>
            )}
          </Box>

          <Box sx={{ ...sharedStyles.verticalContent, gap: "10px" }}>
            {appointmentItem(
                t("appointmentDetails.labels.service"),
              appointment?.serviceEmployee.ServiceStore.serviceName
            )}

            {appointmentItem(
              t("appointmentDetails.labels.store"),
              appointmentEmployee?.store.name
            )}

            {auth.user.role !== UserRole.CLIENT &&
              appointmentItem(
                t("appointmentDetails.labels.client"),
                appointment?.clientName ??
                t("appointmentDetails.placeholders.notAvailable")
              )}

            {appointmentItem(
                  t("appointmentDetails.labels.professional"),
              `${appointmentEmployee?.User.name} ${
                appointmentEmployee?.userId === auth.user.id
                      ? t("appointmentDetails.placeholders.you")
                      : ""
              }`
            )}

            {appointmentItem(
              t("appointmentDetails.labels.date"),
              appointment
                ? formatDateAndTimeDayDateToString(appointment?.date)
                : t("loading")
            )}

            <Box sx={sharedStyles.horizontalContent}>
              <Typography fontWeight={500}>{t("appointmentDetails.labels.status")}</Typography>
              <AppointmentStatusChip status={appointment?.status} />
            </Box>

            {appointment?.cancelationReason &&
              appointment?.status === AppointmentStatus.CANCELED && (
                <Box>
                  <Typography fontWeight={500}>
                    {t("appointmentDetails.labels.cancellationReason")}
                  </Typography>
                  <Typography textAlign="justify">
                    {appointment?.cancelationReason}
                  </Typography>
                </Box>
              )}
          </Box>

          {isAppointmentFinishable && auth.user.role !== UserRole.CLIENT && (
            <PrimaryButton
              label={t("appointmentDetails.actions.finish")}
              icon={<CheckCircleIcon />}
              onClickAction={onFinish}
              isLoading={loading}
              isDisabled={loading}
            />
          )}

          {isAppointmentFinishable && (
            <Box sx={{ ...sharedStyles.verticalContent, gap: "10px" }}>
              <Button
                fullWidth
                sx={{ textTransform: "none" }}
                disableElevation
                endIcon={wantsToCancel ? <ExpandLess /> : <ExpandMore />}
                onClick={() => setWantsToCancel(!wantsToCancel)}
                color="error"
              >
                {t("appointmentDetails.actions.cancelQuestion")}
              </Button>
              <Collapse in={wantsToCancel} timeout="auto" unmountOnExit>
                <Box sx={sharedStyles.verticalContent}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("appointmentDetails.actions.cancelReasonLabel")}
                    rows={2}
                    multiline
                    inputRef={cancellationReasonRef}
                  />
                  <Button
                    fullWidth
                    sx={{ textTransform: "none" }}
                    disableElevation
                    startIcon={<EventBusyIcon />}
                    variant="contained"
                    color="error"
                    onClick={() =>
                      handleCancelAppointmentClick(
                        cancellationReasonRef.current.value,
                        appointment.id,
                        appointment?.serviceEmployeeId
                      )
                    }
                  >
                    {t("appointmentDetails.actions.confirmCancel")}
                  </Button>
                </Box>
              </Collapse>
            </Box>
          )}

          {appointment?.status === AppointmentStatus.FINISHED &&
            auth.user.role === UserRole.CLIENT && (
              <>
                <Box
                  sx={{
                    ...sharedStyles.verticalContent,
                    gap: "10px",
                    alignItems: "center",
                  }}
                >
                  {!loadingRating ? (
                    <>
                      {rating === null || rating?.length === 0 ? (
                        <Box width="100%">
                          <Button
                            fullWidth
                            sx={{ textTransform: "none" }}
                            disableElevation
                            endIcon={
                              wantsToCancel ? <ExpandLess /> : <ExpandMore />
                            }
                            onClick={() => setWantsToCancel(!wantsToCancel)}
                          >
                            {t("appointmentDetails.actions.rateQuestion")}
                          </Button>
                          <Collapse
                            in={wantsToCancel}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box
                              sx={{
                                ...sharedStyles.verticalContent,
                                alignItems: "center",
                              }}
                            >
                              <Rating
                                size="large"
                                value={ratingStars}
                                onChange={(_, newValue) =>
                                  setRatingStars(newValue)
                                }
                              />

                              <TextField
                                fullWidth
                                size="small"
                                label={t("appointmentDetails.rating.commentInput")}
                                rows={2}
                                multiline
                                inputRef={ratingCommentRef}
                              />

                              <PrimaryButton
                                label={t("appointmentDetails.actions.confirmRating")}
                                icon={<CheckIcon />}
                                onClickAction={() =>
                                  handleRateService(
                                    appointment.id,
                                    ratingStars,
                                    ratingCommentRef.current.value
                                  ).then(() => location.reload())
                                }
                              />
                            </Box>
                          </Collapse>
                        </Box>
                      ) : (
                        <>
                          <Typography fontWeight="bold" marginBottom="10px">
                                {t("appointmentDetails.rating.yourRating")}
                          </Typography>
                          <Rating
                            name="read-only"
                            value={rating[0]?.rating}
                            readOnly
                         />

                          {appointmentItem(
                              t("appointmentDetails.labels.comment"),
                              rating[0]?.comment)}
                        </>
                      )}
                    </>
                  ) : (
                    <CircularProgress />
                  )}
                </Box>
              </>
            )}
        </Box>
      </Box>
    </Modal>
  );
};

const styles = {
  container: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    borderRadius: "10px",
    bgcolor: "background.paper",
    p: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",

    "@media (max-width: 600px)": {
      width: 300,
    },
  } as SxProps,
};
